import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";

import api from "@/api/api";
import type {
  CreatePatientFormData,
  UpdatePatientFormData,
} from "@/schemas/patient";
import type { Page } from "@/types/api/page";
import type { Patient, Percentage } from "@/types/api/patient";
import type { Perioquiz } from "@/types/api/perioquiz";
import { addQueryParam } from "@/utils/query";

const getPatient = async (id: number) => {
  const { data } = await api.get<Patient>(`/patients/${id}`);
  return data;
};

export const useGetPatientWithId = (id: number) => {
  return useQuery({
    queryKey: ["patient", id],
    queryFn: () => getPatient(id),
    placeholderData: keepPreviousData,
  });
};

const createPatient = async (data: CreatePatientFormData) => {
  try {
    const res = await api.post<Patient>("/patients", data);
    return res.data;
  } catch (e) {
    if (isAxiosError(e)) {
      throw new Error(e.response?.data || e.message);
    }
    throw e;
  }
};

export const useCreatePatient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPatient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      queryClient.invalidateQueries({ queryKey: ["percentages"] });
    },
  });
};

const updatePatient = (id: number) => {
  return async (data: UpdatePatientFormData) => {
    try {
      const res = await api.put<Patient>(`/patients/${id}`, data);
      return res.data;
    } catch (e) {
      if (isAxiosError(e)) {
        throw new Error(e.response?.data || e.message);
      }
      throw e;
    }
  };
};

export const useUpdatePatient = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePatient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient", id] });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "patient-history-by-attribute" &&
          query.queryKey[2] === id,
      });
    },
  });
};

const patchPatient = (id: number) => {
  return async (data: UpdatePatientFormData) => {
    try {
      const res = await api.patch<Patient>(`/patients/${id}`, data);
      return res.data;
    } catch (e) {
      if (isAxiosError(e)) {
        throw new Error(e.response?.data || e.message);
      }
      throw e;
    }
  };
};

export const usePatchPatient = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchPatient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient", id] });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      queryClient.invalidateQueries({ queryKey: ["percentages"] });
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "patient-history-by-attribute" &&
          query.queryKey[2] === id,
      });
    },
  });
};

const getDoctorsPatients = async (
  page: number,
  take: number,
  nameFilter: string,
  lastId: number | undefined,
  lastConsultationDate: Date | undefined
) => {
  let query = "/patients?";
  query = addQueryParam(query, "page", page);
  query = addQueryParam(query, "take", take);
  query = addQueryParam(query, "order", "desc");
  query = addQueryParam(query, "nameFilter", nameFilter);
  query = addQueryParam(query, "lastId", lastId);
  query = addQueryParam(
    query,
    "lastConsultationDate",
    lastConsultationDate?.toISOString()
  );
  const { data } = await api.get<Page<Patient>>(query);
  return data;
};

export const useGetDoctorsPatients = (
  page: number,
  take: number,
  nameFilter: string,
  lastId: number | undefined,
  lastConsultationDate: Date | undefined
) => {
  return useQuery({
    queryKey: ["patients", page, nameFilter],
    queryFn: () =>
      getDoctorsPatients(page, take, nameFilter, lastId, lastConsultationDate),
    placeholderData: keepPreviousData,
  });
};

const getPatientPercentages = async () => {
  const { data } = await api.get<Array<Percentage>>("/patients/percentages");
  return data;
};

export const useGetPatientPercentages = () => {
  return useQuery({
    queryKey: ["percentages"],
    queryFn: getPatientPercentages,
    placeholderData: keepPreviousData,
  });
};

const getPatientInvitationCode = async (id: number) => {
  const { data } = await api.get<{ code: string }>(`/patients/invite/${id}`);
  return data;
};

export const useGetPatientInvitationCode = (id: number) => {
  return useQuery({
    queryFn: () => getPatientInvitationCode(id),
    queryKey: ["patientInvite", id],
  });
};

const sendPatientInvitation = async (id: number) => {
  const { data } = await api.post(`/patients/invite/${id}`);
  return data;
};

export const useSendPatientInvitation = () => {
  return useMutation({
    mutationFn: sendPatientInvitation,
  });
};

const getPatientPerioquiz = async (id: number) => {
  const { data } = await api.get<Array<Perioquiz>>(`/perioquiz/${id}`);
  return data;
};

export const useGetPatientPerioquiz = (id: number) => {
  return useQuery({
    queryKey: ["perioquiz", id],
    queryFn: () => getPatientPerioquiz(id),
    placeholderData: keepPreviousData,
  });
};
